import React, { useEffect, useState } from "react";
import { baseUrl } from '../shared.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Box,
  Typography,
  TableSortLabel,
  TextField,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { StatusChip } from '../components/ui';

function OpenBookings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState({});
  const [orderBy, setOrderBy] = useState("assetId");
  const [order, setOrder] = useState("asc");

  // Filters
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const url = baseUrl+'hire/list/open/'
    fetch(url, {
      method: 'POST',
      headers: {
          'Content-Type': 'application/json',
          'username': localStorage.getItem('username'),
          'bToken': localStorage.getItem('bToken'),
      },
  })
      .then(res => res.json())
      .then(data => setBookings(data));
  }, []);

  const handleToggle = (fullName) => {
    setOpen(prev => ({ ...prev, [fullName]: !prev[fullName] }));
  };

  // Sorting helpers
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortData = (array) => {
    return [...array].sort((a, b) => {
      if (a[orderBy] < b[orderBy]) {
        return order === "asc" ? -1 : 1;
      }
      if (a[orderBy] > b[orderBy]) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  // Filtering
  const filteredBookings = bookings.filter(r => {
    const statusMatch = statusFilter ? r.status === statusFilter : true;
    
    return statusMatch;
  });

  // Group by customer after filtering
  const grouped = filteredBookings.reduce((acc, booking) => {
    if (!acc[booking.fullName]) {
      acc[booking.fullName] = [];
    }
    acc[booking.fullName].push(booking);
    return acc;
  }, {});

  // Unique statuses for filter dropdown
  const allStatuses = [...new Set(bookings.map(r => r.status))];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', mb: 0.5 }}>Open Bookings</Box>
      <Box sx={{ fontSize: 12, color: 'var(--muted)', mb: 2 }}>Current bookings grouped by customer.</Box>

      {/* Filters */}
      <Box sx={{ mb: 2, maxWidth: 260 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {allStatuses.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Grouped by customer: full-width clickable header opens the panel below. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Object.entries(grouped).map(([fullName, fullNameBookings]) => (
          <Box key={fullName} sx={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
            {/* Clickable customer header (no arrow) */}
            <Box
              onClick={() => handleToggle(fullName)}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                px: 2, py: 1.5, cursor: 'pointer',
                background: open[fullName] ? 'var(--surface-2)' : 'transparent',
                '&:hover': { background: 'var(--surface-2)' },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--text)' }}>{fullName}</Typography>
              <Box sx={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>
                {fullNameBookings.length} booking{fullNameBookings.length === 1 ? '' : 's'}
              </Box>
            </Box>

            {/* Collapsible content: cards on mobile, table on desktop */}
            <Collapse in={open[fullName]} timeout="auto" unmountOnExit>
              <Box sx={{ borderTop: '1px solid var(--border)' }}>
                {isMobile ? (
                  <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {sortData(fullNameBookings).map((r, i) => {
                      const goToBooking = () => { window.location.href = `${window.location.origin}?booking=${r.hyperlink}`; };
                      return (
                        <Box key={i} onClick={goToBooking} sx={{ cursor: 'pointer', p: 1.5, borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                            <Box className="app-mono" sx={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{r.itemNo}</Box>
                            <StatusChip domain="booking" value={r.status} dot />
                          </Box>
                          <Box sx={{ fontSize: 12.5, color: 'var(--text)' }}>{r.startDate} to {r.returnDate}</Box>
                          <Box sx={{ fontSize: 11.5, color: 'var(--muted)', mt: 0.25 }}>
                            PO: {r.poNumber || '—'} · Invoice: {r.invoiceNumber || '—'}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {["itemNo", "startDate", "returnDate", "poNumber","invoiceNumber", "status"].map((col) => (
                            <TableCell key={col}>
                              <TableSortLabel
                                active={orderBy === col}
                                direction={orderBy === col ? order : "asc"}
                                onClick={() => handleSort(col)}
                              >
                                {col === "itemNo" && "Item Number"}
                                {col === "startDate" && "Start Date"}
                                {col === "returnDate" && "Return Date"}
                                {col === "poNumber" && "PO Number"}
                                {col === "invoiceNumber" && "Invoice Number"}
                                {col === "status" && "Booking Status"}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortData(fullNameBookings).map((r, i) => (
                          <TableRow
                                key={i}
                                hover
                                sx={{ cursor: "pointer" }}
                                onClick={() => {
                                  const baseUrl = window.location.origin;
                                  window.location.href = `${baseUrl}?booking=${r.hyperlink}`;
                                }}
                          >
                            <TableCell>{r.itemNo}</TableCell>
                            <TableCell>{r.startDate}</TableCell>
                            <TableCell>{r.returnDate}</TableCell>
                            <TableCell>{r.poNumber}</TableCell>
                            <TableCell>{r.invoiceNumber}</TableCell>
                            <TableCell><StatusChip domain="booking" value={r.status} dot /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default OpenBookings;